import {
	Fragment,
	ReactElement,
	useCallback
} from "react";
import {
	Button,
	DateString,
	FormattedText,
	Space,
	Typography
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { setTrackingId } from "client/Misc/utils";
import { never } from "client_legacy/ndaw/errors";
import {
	RemoveEventFromCalendarResult,
	UpdateEventInCalendarResult,
	registerInCalendar,
	removeFromCalendar
} from "client/Api/Hub/Events/eventViewer";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation,
	useUserTimezone
} from "client/Hooks";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { TimeZone } from "client/DateAndTime/timezones";
import { EventAttendanceNotificationDataViewModel, EventAttendanceNotificationKind } from "client/Api/Notifications/notifications";

export type EventAttendanceActivityProps = {
	data: EventAttendanceNotificationDataViewModel;
};

export const EventAttendanceActivity = (props: EventAttendanceActivityProps): ReactElement => {
	const { data } = props;

	const {
		eventId,
		title
	} = data;

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const userTimezone: TimeZone = useUserTimezone();

	const processRegisterInCalendarResult = useCallback(
		(result: UpdateEventInCalendarResult): void => {
			switch (result) {
				case UpdateEventInCalendarResult.NotUpdated:
					break;
				case UpdateEventInCalendarResult.Success:
					alertManager.showSuccess(localize("_EventIsUpdatedInYourCalendar"));
					break;
				case UpdateEventInCalendarResult.Unknown:
				case UpdateEventInCalendarResult.Failure:
					alertManager.showWarning(localize("_FailedToUpdateThisEventInYourCalendarPleaseTryAgain"));
					break;
				default:
					never(result);
			}
		},
		[alertManager]
	);

	const addToCalendarHandler = useCallback(
		async (): Promise<void> => {
			try {
				const result: UpdateEventInCalendarResult = await registerInCalendar(eventId, globalCancellation);
				processRegisterInCalendarResult(result);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				processRegisterInCalendarResult(UpdateEventInCalendarResult.Failure);
			}
		},
		[
			globalCancellation,
			processRegisterInCalendarResult,
			eventId
		]
	);

	const processRemoveFromCalendarResult = useCallback(
		(result: RemoveEventFromCalendarResult): void => {
			switch (result) {
				case RemoveEventFromCalendarResult.Success:
					alertManager.showSuccess(localize("_EventIsRemovedFromYourCalendar"));
					break;
				case RemoveEventFromCalendarResult.Unknown:
				case RemoveEventFromCalendarResult.Failure:
					alertManager.showWarning(
						localize("_FailedToRemoveThisEventFromYourCalendarPleaseTryAgain")
					);
					break;
				default:
					never(result);
			}
		},
		[alertManager]
	);
	const removeFromCalendarHandler = useCallback(
		async (): Promise<void> => {
			try {
				const result: RemoveEventFromCalendarResult = await removeFromCalendar(eventId, globalCancellation);
				processRemoveFromCalendarResult(result);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				processRemoveFromCalendarResult(RemoveEventFromCalendarResult.Failure);
			}
		},
		[
			globalCancellation,
			processRemoveFromCalendarResult,
			eventId
		]
	);

	switch (data.kind) {
		case EventAttendanceNotificationKind.Removed:
			return (
				<span {...setTrackingId("title")}>
					<FormattedText
						text={localize("_CancelledEventTitleX")}
						thingsToReplace={[
							{
								what: "{{Title}}",
								filler: (
									<Typography
										weight="bold"
										trackingId="event-title"
									>
										{title}
									</Typography>
								)
							}
						]}
					/>
				</span>
			);
		case EventAttendanceNotificationKind.Updated:
			const changedPropertiesLength: number = data
				.changedProperties
				.length;

			return (
				<>
					<div {...setTrackingId("title")}>
						<FormattedText
							text={localize("_ModifiedEventX")}
							thingsToReplace={[
								{
									what: "{{Title}}",
									filler: (
										<Typography
											weight="bold"
											trackingId="event-title"
										>
											{title}
										</Typography>
									)
								}
							]}
						/>
						<Space />
						{data.changedProperties
							.map(
								(x, index) => (
									<Fragment key={index}>
										{localize(x)}
										{index < changedPropertiesLength - 1
											? index === changedPropertiesLength - 2
												? ` ${localize("_And")} `
												: ", "
											: ""
										}
									</Fragment>
								)
							)
						}
					</div>
					<Button
						icon="arrows-rotate"
						kind="transparent"
						text={localize("_UpdateInCalendar")}
						onClick={addToCalendarHandler}
						bubbleClick={false}
						trackingId="update-in-calendar"
					/>
				</>
			);
		case EventAttendanceNotificationKind.Reminder:
			return (
				<span {...setTrackingId("title")}>
					<FormattedText
						text={localize("_ReminderForEventTitleXStartDateYEndDateZ")}
						thingsToReplace={[
							{
								what: "{{Title}}",
								filler: (
									<Typography
										weight="bold"
										trackingId="event-title"
									>
										{title}
									</Typography>
								)
							},
							{
								what: "{{StartDate}}",
								filler: (
									<DateString
										value={data.startDate}
										format="RelativeDayWithTime"
										timezone={userTimezone}
									/>
								)
							},
							{
								what: "{{EndDate}}",
								filler: (
									<DateString
										value={data.endDate}
										format="RelativeDayWithTime"
										timezone={userTimezone}
									/>
								)
							}
						]}
					/>
				</span>
			);
			case EventAttendanceNotificationKind.RemovedAttendee:
				return (
					<>
						<div {...setTrackingId("title")}>
							<FormattedText
								text={localize("_RemovedAttendeeFromEventX")}
								thingsToReplace={[
									{
										what: "{{Title}}",
										filler: (
											<Typography
												weight="bold"
												trackingId="event-title"
											>
												{title}
											</Typography>
										)
									}
								]}
							/>
						</div>
						<Button
							icon="trash-can"
							kind="transparent"
							text={localize("_RemoveFromCalendar")}
							onClick={removeFromCalendarHandler}
							bubbleClick={false}
							trackingId="remove-from-calendar"
						/>
					</>
				);
		default:
			never(data);
	}
};

EventAttendanceActivity.displayName = "EventAttendanceActivity";